import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { setCurrentAdmin } from '../services/logger';
import { logAction } from '../services/logger';

export interface AdminUser {
  email: string;
  name: string;
  role: 'super_admin' | 'admin';
}

export function useAuth() {
  const [admin, setAdmin] = useState<AdminUser | null>(null);
  const [loading, setLoading] = useState(true);

  const checkSession = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user?.email) {
        const { data: adminUser } = await supabase
          .from('admin_users')
          .select('email, name, role')
          .eq('email', session.user.email)
          .single();

        if (adminUser) {
          const typed: AdminUser = {
            email: adminUser.email,
            name: adminUser.name,
            role: adminUser.role as 'super_admin' | 'admin',
          };
          setAdmin(typed);
          setCurrentAdmin({ email: typed.email, name: typed.name });
        } else {
          // Not in admin_users → sign out immediately
          await supabase.auth.signOut();
          setAdmin(null);
        }
      }
    } catch {
      setAdmin(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!session) {
        setAdmin(null);
        setCurrentAdmin(null);
      }
    });

    return () => subscription.unsubscribe();
  }, [checkSession]);

  const login = async (email: string, password: string): Promise<string | null> => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) return error.message;

    // Verify admin_users membership
    const { data: adminUser } = await supabase
      .from('admin_users')
      .select('email, name, role')
      .eq('email', email)
      .single();

    if (!adminUser) {
      await supabase.auth.signOut();
      return 'Accès non autorisé — ce compte n\'est pas administrateur';
    }

    const typed: AdminUser = {
      email: adminUser.email,
      name: adminUser.name,
      role: adminUser.role as 'super_admin' | 'admin',
    };

    setAdmin(typed);
    setCurrentAdmin({ email: typed.email, name: typed.name });
    await logAction('login', null, null, null, { email, role: typed.role }, `${typed.name} s'est connecté au back-office`);
    return null;
  };

  const logout = async () => {
    await supabase.auth.signOut();
    setAdmin(null);
    setCurrentAdmin(null);
  };

  /**
   * Role-based permission check.
   * super_admin → can do everything
   * admin → can do everything EXCEPT delete bars/matches
   */
  const canDelete = admin?.role === 'super_admin';
  const isSuperAdmin = admin?.role === 'super_admin';

  return { admin, loading, login, logout, canDelete, isSuperAdmin };
}
